/// <reference types="google-maps" />

import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import CustomError from 'Douleutaras/components/CustomError/CustomError';
import { AddressAutocompleteField } from 'Douleutaras/components/Forms/Fields/AddressAutocompleteField';
import { Gis } from 'Douleutaras/components/Forms/gis.class';

import { LocationMap } from './LocationMap';
import './LocationMapPanel.scss';

export interface LocationMapPanelProps {
  // Text shown in the autocomplete input (the picked address description).
  addressValue?: string;
  // Center for the map, derived from the currently picked location.
  center?: google.maps.LatLngLiteral;
  // When true, the provided center is reverse-geocoded and validated immediately
  // (used when the center comes from the user's device location).
  resolveCenter?: boolean;
  // Error message shown when a confirmed area is not served.
  errorMessage?: string;
  // Fired when the user picks a location, either by autocomplete or by dropping the map.
  onPick(gis: Gis): void;
}

/**
 * Inline map picker rendered as a view state of the location step. Lets the user
 * pick a location by searching an address or by dragging an interactive map. The
 * selection is confirmed via the modal action button and dismissed via the modal
 * close button (see PostJobModal footer/header).
 */
export const LocationMapPanel = ({
                                   addressValue,
                                   center,
                                   resolveCenter,
                                   errorMessage,
                                   onPick
}: LocationMapPanelProps) => {
  const { t } = useTranslation();

  return (
    <div styleName="location-map-panel">
      <div styleName="location-map-panel__head">
        <span styleName="location-map-panel__title">
          {t('T.POST-JOB.MAP.TITLE', 'Choose on the map')}
        </span>
      </div>
      <div styleName={classNames('location-map-panel__field', {
             'location-map-panel__field--error': Boolean(errorMessage)
           })}>
        <AddressAutocompleteField input={{
                                    name: 'map-address',
                                    value: addressValue,
                                    className: 'custom-input',
                                    placeholder: t('T.POST-JOB.PH.Address')
                                  }}
                                  onChange={onPick}
        />
        {errorMessage &&
          <CustomError>{errorMessage}</CustomError>
        }
      </div>
      <LocationMap center={center} resolveCenter={resolveCenter} onChange={onPick} />
    </div>
  );
};

export default LocationMapPanel;
